/**
 * Modal dialog focus helpers (R3-L11, SPA-12): shared by every dialog that says `aria-modal`.
 */

export const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * While the dialog is open everything else on the page is `inert` (no focus, no clicks, hidden
 * from assistive tech); returns a function that restores what was there before.
 *
 * R-21: content mounted on the page while the dialog is open (a portal, a toast) is made inert
 * too, except another modal dialog opened on top, which manages the page itself.
 */
export function makeBackgroundInert(dialogRoot: HTMLElement): () => void {
  const changed = new Set<Element>();
  const makeInert = (el: Element) => {
    if (el === dialogRoot || el.contains(dialogRoot) || el.hasAttribute("inert")) return;
    changed.add(el);
    el.setAttribute("inert", "");
  };
  for (const el of Array.from(document.body.children)) makeInert(el);

  const observer =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver((records) => {
          for (const record of records) {
            for (const node of Array.from(record.addedNodes)) {
              if (!(node instanceof Element)) continue;
              const modal =
                node.matches('[aria-modal="true"]') || node.querySelector('[aria-modal="true"]');
              if (!modal) makeInert(node);
            }
          }
        });
  observer?.observe(document.body, { childList: true });

  return () => {
    observer?.disconnect();
    for (const el of changed) el.removeAttribute("inert");
  };
}

/** Keeps Tab / Shift+Tab inside `root` (call from the dialog's keydown handler). */
export function wrapTabFocus(e: React.KeyboardEvent, root: HTMLElement | null): void {
  if (e.key !== "Tab" || !root) return;
  const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/** W-30: asks every player that fills the window to go back into the page. */
export const LEAVE_FULLSCREEN_EVENT = "lms:leave-fullscreen";

/**
 * W-30: a dialog portalled to `<body>` is not painted while another element is in native full
 * screen, and a window-filling player (`z-[1000]`) sits above it. A modal that must be seen (the
 * idle "Still there?") calls this first.
 */
export function leaveFullscreen(): void {
  if (typeof document.exitFullscreen === "function" && document.fullscreenElement) {
    void document.exitFullscreen().catch(() => undefined);
  }
  window.dispatchEvent(new Event(LEAVE_FULLSCREEN_EVENT));
}
